import { MenuList } from '../layout/nav/MenuList';

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

export const getStoredPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem('permissions') || '{}');
  } catch {
    return {};
  }
};

// Only Superadmin bypasses permission checks by default. Admin is now a
// normal permission-based role, same as Manager/Waiter/Checkout — it needs
// modules explicitly granted via the User Permission screen.
export const isAdminRole = (role) => {
  return role === 'superadmin';
};

// "Superadmin" is an internal/bootstrap concept (created once, automatically,
// when a restaurant itself is created) — restaurant staff should only ever
// see it called "Admin", same label used for the separate, editable Admin
// role staff can actually be assigned. Both showing as "Admin" is
// intentional: from a restaurant's own perspective there's just one
// recognizable top-tier label, even though the two remain different roles
// internally (Superadmin's access can never be restricted, Admin's can).
export const getRoleDisplayLabel = (role) => {
  if (role === 'superadmin') return 'Admin';
  if (!role) return '-';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

// The Platform Owner is a superadmin with NO restaurant_id — every normal
// restaurant's own superadmin always has one, so sharing the "superadmin"
// role name is safe: restaurant_id is what actually gates Manage
// Restaurants, checked before the normal isAdminRole bypass below.
export const isPlatformOwnerRole = (user) =>
  user?.role === 'superadmin' && !user?.restaurant_id;

export const canViewModule = (moduleKey) => {
  const user = getStoredUser();
  const permissions = getStoredPermissions();

  if (!user) return false;
  if (moduleKey === 'manage_restaurants') return isPlatformOwnerRole(user);
  if (isAdminRole(user.role)) return true;
  if (!moduleKey) return true;

  return !!permissions?.[moduleKey]?.can_view;
};

export const canCreateModule = (moduleKey) => {
  const user = getStoredUser();
  const permissions = getStoredPermissions();

  if (!user) return false;
  if (isAdminRole(user.role)) return true;

  return !!permissions?.[moduleKey]?.can_create;
};

export const canUpdateModule = (moduleKey) => {
  const user = getStoredUser();
  const permissions = getStoredPermissions();

  if (!user) return false;
  if (isAdminRole(user.role)) return true;

  return !!permissions?.[moduleKey]?.can_update;
};

export const canDeleteModule = (moduleKey) => {
  const user = getStoredUser();
  const permissions = getStoredPermissions();

  if (!user) return false;
  if (isAdminRole(user.role)) return true;

  return !!permissions?.[moduleKey]?.can_delete;
};

const flattenMenuItems = (items) =>
  items.reduce((flat, item) => {
    if (item.children?.length) {
      return [...flat, ...flattenMenuItems(item.children)];
    }
    if (item.module_key && item.to) flat.push(item);
    return flat;
  }, []);

// Every route in this app requires a specific module permission — there's
// no universal "safe" landing page. This finds the first one a user can
// actually see, so login/redirects don't have to guess (or hardcode
// /dashboard, which breaks for anyone without dashboard access).
export const getFirstAllowedRoute = (user, permissions) => {
  if (!user) return null;
  if (isPlatformOwnerRole(user)) return '/manage-restaurants';
  if (isAdminRole(user.role)) return '/dashboard';

  const flat = flattenMenuItems(MenuList);

  const firstAllowed = flat.find(
    (item) => permissions?.[item.module_key]?.can_view
  );

  return firstAllowed ? firstAllowed.to : null;
};